/**
 * Icon Resolver
 *
 * Resolves icon references to React components.
 *
 * Supports:
 * - IconConfig objects: { name: 'Archive', size: 20 }
 * - React elements: <CustomIcon />
 */
import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import * as SynapseIcons from '@lognai/synapse-design-system-v2/icons';
import type { IconConfig } from '../types/route-config';

/**
 * Resolves a name against the legacy Lucide-compat aliases first, then against
 * Phosphor's own namespace. Compat wins so that existing string call sites keep
 * their current glyph even where a same-spelled Phosphor icon exists but means
 * something different.
 */
function resolveGlyph(name: string): React.ElementType {
  const glyph =
    (SynapseIcons as Record<string, unknown>)[name] ??
    (PhosphorIcons as Record<string, unknown>)[name];
  if (glyph) return glyph as React.ElementType;
  console.warn(`[Icon] Unknown icon name: "${name}". Falling back to Circle.`);
  return PhosphorIcons.Circle;
}

interface NavIconProps {
  name: string;
  size?: number;
  weight?: PhosphorIcons.IconWeight;
}

/**
 * Renders a glyph by name. Kept as its own element, rather than returning the
 * glyph directly, so the name stays on the element's props and the rail can
 * still set `weight` by cloning it.
 */
function NavIcon({ name, ...props }: NavIconProps): React.ReactElement {
  return React.createElement(resolveGlyph(name), props);
}

/**
 * Check if value is an IconConfig object
 */
function isIconConfig(icon: any): icon is IconConfig {
  return (
    typeof icon === 'object' &&
    icon !== null &&
    'name' in icon &&
    typeof icon.name === 'string'
  );
}

/**
 * Resolve an icon to a React element
 *
 * Accepts:
 * - IconConfig object: { name: 'Archive', size: 20 }
 * - React element: <CustomIcon />
 * - undefined: returns default Circle icon
 *
 * @param icon - Icon config object or React element
 * @param defaultSize - Default icon size (default: 20)
 * @returns React element for the icon
 */
export function resolveIconComponent(
  icon?: IconConfig | React.ReactElement,
  defaultSize: number = 20
): React.ReactElement {
  // No icon provided, use default
  if (!icon) {
    return React.createElement(NavIcon, { name: 'Circle', size: defaultSize });
  }

  // Icon config object
  if (isIconConfig(icon)) {
    return React.createElement(NavIcon, {
      name: icon.name,
      size: icon.size ?? defaultSize,
    });
  }

  // Already a React element
  if (React.isValidElement(icon)) {
    return icon;
  }

  // Invalid type
  throw new Error(
    `Icon must be an IconConfig object or React element. ` +
      `Received: ${typeof icon}. ` +
      `Example: icon: { name: 'Archive', size: 20 }`
  );
}

